import { useState } from "react";
import { Download, Trash2, Upload, Loader2, Lock, Globe, Camera } from "lucide-react";
import { Camera as CapCamera, CameraResultType, CameraSource } from "@capacitor/camera";
import { isNative } from "@/lib/native";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatErr, fmtDate, downloadDoc } from "@/lib/api";

export const DOC_CATEGORIES = {
  business: ["Business Registration", "Business License", "Insurance", "Contract", "Job Description", "Policy / Handbook", "Other"],
  candidate: ["Resume", "License", "Certification", "Police Check", "ID", "Reference", "Portfolio", "Other"],
};

export const fmtBytes = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`);

export const UploadForm = ({ ownerType, onUploaded, extra = {}, compact = false }) => {
  const cats = DOC_CATEGORIES[ownerType];
  const [f, setF] = useState({ title: "", category: cats[0], shared: false });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [key, setKey] = useState(0);

  const snap = async () => {
    try {
      const p = await CapCamera.getPhoto({ resultType: CameraResultType.Uri, source: CameraSource.Prompt, quality: 85 });
      const blob = await (await fetch(p.webPath)).blob();
      setFile(new File([blob], `photo-${Date.now()}.${p.format || "jpg"}`, { type: blob.type || "image/jpeg" }));
    } catch (e) { if (!/cancel/i.test(e?.message || "")) toast.error("Couldn't open the camera"); }
  };
  const submit = async () => {
    if (!file) return toast.error("Choose a file first");
    const fd = new FormData();
    fd.append("title", f.title || file.name);
    fd.append("category", f.category);
    fd.append("visibility", f.shared ? "shared" : "private");
    Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/documents", fd);
      onUploaded(data);
      setF({ ...f, title: "" });
      setFile(null);
      setKey((k) => k + 1);
      toast.success("File uploaded securely");
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  return (
    <div data-testid="doc-upload-form" className={`space-y-3 rounded-md border border-stone-200 bg-[#F5F5F0] ${compact ? "p-4" : "p-6"}`}>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5"><Label className="text-xs">Title</Label><Input data-testid="doc-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. First Aid Certificate" className="bg-white" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Category</Label>
          <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
            <SelectTrigger data-testid="doc-category-select" className="bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <Input key={key} data-testid="doc-file-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="bg-white" />
      {isNative && (
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" variant="outline" size="sm" data-testid="doc-camera-button" onClick={snap} className="bg-white"><Camera className="mr-2 h-4 w-4" />Take a photo</Button>
          {file && <span data-testid="doc-selected-file" className="truncate text-xs text-stone-600">{file.name}</span>}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-stone-600">
          <Switch data-testid="doc-shared-switch" checked={f.shared} onCheckedChange={(v) => setF({ ...f, shared: v })} />
          {f.shared ? (ownerType === "business" ? "Visible to candidates you contact" : "Visible to member businesses") : "Private (only you, our team & approved requests)"}
        </label>
        <Button data-testid="doc-upload-button" disabled={busy} onClick={submit}>{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Upload</Button>
      </div>
    </div>
  );
};

export const DocRow = ({ d, onChange, onDelete, editable }) => {
  const toggle = async (v) => {
    try {
      const { data } = await api.patch(`/documents/${d.id}`, { visibility: v ? "shared" : "private" });
      onChange?.(data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const del = async () => {
    try { await api.delete(`/documents/${d.id}`); onDelete?.(d); toast.success("File removed"); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div data-testid={`doc-row-${d.id}`} className="flex flex-wrap items-center gap-3 rounded-md border border-stone-200 bg-white px-4 py-3">
      {d.visibility === "shared" ? <Globe className="h-4 w-4 text-emerald-600" /> : <Lock className="h-4 w-4 text-stone-400" />}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-stone-900">{d.title}</p>
        <p className="text-[11px] font-mono uppercase text-stone-500">{d.category} · {fmtBytes(d.size)} · {fmtDate(d.created_at)}{d.owner_name ? ` · ${d.owner_name}` : ""}</p>
      </div>
      {editable && <Switch data-testid={`doc-visibility-${d.id}`} checked={d.visibility === "shared"} onCheckedChange={toggle} title="Shared" />}
      <Button size="icon" variant="ghost" data-testid={`doc-download-${d.id}`} onClick={() => downloadDoc(d)}><Download className="h-4 w-4" /></Button>
      {editable && <Button size="icon" variant="ghost" data-testid={`doc-delete-${d.id}`} onClick={del} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>}
    </div>
  );
};
